// Colleagues' presence, as the live connection reports it (ADR 0008).
//
// The browser asks only for the people whose badge is on screen: each badge
// "watches" its person while mounted, and the set of watched people — at most
// 500 — is sent to the server when it settles. There is no way to ask for
// anybody else (FR-PRES-05).

import { create } from 'zustand';
import type { PresenceState } from './tokens';

export interface CustomStatus {
  emoji?: string;
  text?: string;
  /** ISO 8601. Absent: until the person clears it. */
  expires_at?: string;
}

/** How one person shows — the server's word, before local expiry is applied. */
export interface Presence {
  user_id: string;
  state: PresenceState;
  /** Set when state is dnd: when it ends, and what the person shows after. */
  dnd_until?: string;
  after_dnd?: PresenceState;
  status?: CustomStatus;
}

/** The server's cap on one connection's watch list (realtime.MaxWatched). */
export const MAX_WATCHED = 500;
/** Badges mount and unmount in bursts while a list scrolls; wait for it to settle. */
const SETTLE_MS = 400;

interface PresenceStore {
  people: Record<string, Presence>;
  apply: (list: Presence[]) => void;
}

export const usePresenceStore = create<PresenceStore>((set) => ({
  people: {},
  apply: (list) =>
    set((s) => {
      const people = { ...s.people };
      for (const p of list) {
        if (p && typeof p.user_id === 'string' && typeof p.state === 'string') people[p.user_id] = p;
      }
      return { people };
    }),
}));

// ---- watching ------------------------------------------------------------

const watched = new Map<string, number>();
let sender: ((ids: string[]) => void) | null = null;
let lastSent = '';
let timer: ReturnType<typeof setTimeout> | null = null;

/** Put a person on screen. Returns the function that takes them off. */
export function watch(userId: string): () => void {
  watched.set(userId, (watched.get(userId) ?? 0) + 1);
  schedule();
  return () => {
    const n = (watched.get(userId) ?? 1) - 1;
    if (n <= 0) watched.delete(userId);
    else watched.set(userId, n);
    schedule();
  };
}

function schedule(): void {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, SETTLE_MS);
}

/**
 * Send the watch list if it changed. Hidden tabs send nothing: the tab the
 * person is looking at decides what the connection watches, and a tab that
 * becomes visible sends its own list again (resendWatchList).
 */
function flush(): void {
  if (!sender) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  // Newest last, so a list over the cap keeps the most recently shown people.
  const ids = [...watched.keys()].slice(-MAX_WATCHED);
  const key = ids.join(',');
  if (key === lastSent) return;
  lastSent = key;
  sender(ids);
}

/** Start sending watch lists through `send`. Returns the function that stops. */
export function connectWatchList(send: (ids: string[]) => void): () => void {
  sender = send;
  lastSent = '';
  return () => {
    if (sender === send) sender = null;
  };
}

/** Send the list again regardless: a new connection, or this tab came forward. */
export function resendWatchList(): void {
  lastSent = '';
  flush();
}

/** For tests. */
export function resetPresence(): void {
  watched.clear();
  sender = null;
  lastSent = '';
  if (timer !== null) clearTimeout(timer);
  timer = null;
  usePresenceStore.setState({ people: {} });
}

/** For tests: the current watch list. */
export function watchedIds(): string[] {
  return [...watched.keys()];
}

// ---- expiry --------------------------------------------------------------

/**
 * Apply what has ended since the server spoke: do-not-disturb past its end
 * becomes what the person shows after it, and an expired custom status
 * disappears. No event is sent for either, so the browser does it itself.
 */
export function effective(p: Presence, now = Date.now()): Presence {
  let out = p;
  if (p.state === 'dnd' && p.dnd_until && Date.parse(p.dnd_until) <= now) {
    out = { user_id: p.user_id, state: p.after_dnd ?? 'active', ...(p.status ? { status: p.status } : {}) };
  }
  if (out.status?.expires_at && Date.parse(out.status.expires_at) <= now) {
    const { status: _expired, ...rest } = out;
    out = rest;
  }
  return out;
}

/** Milliseconds until effective() would answer differently, or null for never. */
export function nextChange(p: Presence, now = Date.now()): number | null {
  const times = [p.state === 'dnd' ? p.dnd_until : undefined, p.status?.expires_at]
    .filter((t): t is string => Boolean(t))
    .map((t) => Date.parse(t) - now)
    .filter((ms) => ms > 0);
  return times.length > 0 ? Math.min(...times) : null;
}
